import { useCallback, useMemo, useReducer } from 'react';
import type { ZodType } from 'zod';
import { mapValuesForWrite } from './mapValuesForWrite';
import { formReducer, initialState } from './reducer';
import { evalRules } from './rules';
import type {
  FieldConfig,
  FieldRuntimeProps,
  FormConfig,
  FormState,
} from './types';

const makeValidate =
  <TCtx>(controls: FieldConfig<TCtx>[], schema?: ZodType) =>
  (
    values: Record<string, unknown>,
    ctx: TCtx,
    state: FormState
  ): Record<string, string | null> => {
    const showValidation =
      state.submitCount > 0 || Object.keys(state.touched).length > 0;
    const errs: Record<string, string | null> = {};
    for (const c of controls) {
      if (typeof c.validation === 'function') {
        errs[c.id] = c.validation(values[c.id], { values, ctx, showValidation });
      }
    }
    if (schema) {
      const mapped = mapValuesForWrite(controls, values, ctx, true); // для валидации всегда включаем все поля
      const res = schema.safeParse(mapped);
      if (!res.success) {
        for (const issue of res.error.issues) {
          const key = String(issue.path[0]);
          errs[key] ||= issue.message;
        }
      }
    }
    return errs;
  };

export function useFormModel<TCtx>({
  config,
  ctx,
}: {
  config: FormConfig<TCtx>;
  ctx: TCtx;
}) {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const validate = useMemo(
    () => makeValidate(config.controls, config.validation),
    [config.controls, config.validation]
  );

  const runtimeById = useMemo(() => {
    const bag = { values: state.values, ctx };
    const acc: Record<string, FieldRuntimeProps> = {};
    for (const c of config.controls) acc[c.id] = evalRules(c.rules, bag);

    return acc;
  }, [state.values, ctx, config.controls]);

  const onChange = useCallback((id: string, value: unknown) => {
    dispatch({ type: 'fieldChanged', id, value });
  }, []);

  const onBlur = useCallback((id: string) => {
    dispatch({ type: 'fieldBlurred', id });
  }, []);

  const runValidate = useCallback(() => {
    const errors = validate(state.values, ctx, state);
    dispatch({ type: 'setErrors', errors });
    return errors;
  }, [validate, state.values, state.touched, state.submitCount, ctx]);

  const submit = useCallback(
    async (handler: (finalModel: TCtx) => Promise<void> | void) => {
      dispatch({ type: 'incrementSubmit' });
      const errors = runValidate();
      if (Object.values(errors).some(Boolean)) return;
      try {
        dispatch({ type: 'submitting', value: true });
        const finalModel = mapValuesForWrite(
          config.controls,
          state.values,
          ctx,
          config.includeUnchangedFields ?? false
        );
        await handler(finalModel);
      } finally {
        dispatch({ type: 'submitting', value: false });
      }
    },
    [config.controls, state.values, ctx, runValidate]
  );

  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  return {
    state,
    runtimeById,
    onChange,
    onBlur,
    runValidate,
    submit,
    reset,
    config,
    ctx,
  };
}
